// Push Notification Service
// خدمة الإشعارات المحلية

class PushNotificationService {
  constructor() {
    this.permission = 'default';
    this.swRegistration = null;
  }

  // Check if browser supports notifications
  isSupported() {
    return 'Notification' in window && 'serviceWorker' in navigator;
  }

  // Initialize service worker and request permission
  async init() {
    if (!this.isSupported()) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      // Register service worker
      this.swRegistration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', this.swRegistration);

      // Request notification permission
      this.permission = await this.requestPermission();
      
      return this.permission === 'granted';
    } catch (error) {
      console.error('Error initializing push notifications:', error);
      return false;
    }
  }

  // Request permission from user
  async requestPermission() {
    if (Notification.permission === 'granted') {
      return 'granted';
    }
    
    if (Notification.permission === 'denied') {
      return 'denied';
    }

    const permission = await Notification.requestPermission();
    this.permission = permission;
    return permission;
  }

  // Show notification
  async show(title, options = {}) {
    if (this.permission !== 'granted') {
      const granted = await this.init();
      if (!granted) {
        console.log('Notification permission not granted');
        return false;
      }
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [200, 100, 200],
      requireInteraction: true,
      tag: 'helcoo-' + Date.now(),
      renotify: true,
      silent: false,
      ...options
    };

    try {
      // Try using service worker notification (works in background)
      if (this.swRegistration) {
        await this.swRegistration.showNotification(title, defaultOptions);
      } else {
        // Fallback to regular notification
        new Notification(title, defaultOptions);
      }
      return true;
    } catch (error) {
      console.error('Error showing notification:', error);
      // Fallback to regular notification
      try {
        new Notification(title, defaultOptions);
        return true;
      } catch (e) {
        console.error('Fallback notification failed:', e);
        return false;
      }
    }
  }

  // Show follow-up reminder notification
  async showFollowUpReminder(leadName, followUpType, url = '/pipeline') {
    const typeLabels = {
      call: 'مكالمة',
      meeting: 'اجتماع',
      email: 'بريد إلكتروني',
      whatsapp: 'واتساب',
      site_visit: 'زيارة موقع',
      other: 'أخرى'
    };

    return this.show(`تذكير متابعة - ${leadName}`, {
      body: `حان موعد ${typeLabels[followUpType] || 'المتابعة'} مع ${leadName}`,
      tag: 'followup-' + Date.now(),
      data: { url },
      actions: [
        { action: 'open', title: 'فتح' },
        { action: 'dismiss', title: 'تأجيل' }
      ]
    });
  }

  // Show new lead notification
  async showNewLead(leadName, source, url = '/leads') {
    const sourceLabels = {
      whatsapp: 'واتساب',
      facebook: 'فيسبوك',
      instagram: 'انستغرام',
      manual: 'يدوي'
    };

    return this.show('عميل جديد!', {
      body: `${leadName} من ${sourceLabels[source] || source}`,
      tag: 'newlead-' + Date.now(),
      data: { url }
    });
  }

  // Show assignment notification
  async showAssignment(leadName, url = '/pipeline') {
    return this.show('تم تعيين عميل لك!', {
      body: `تم تعيين العميل ${leadName} إليك`,
      tag: 'assignment-' + Date.now(),
      data: { url }
    });
  }

  // Show rotation warning
  async showRotationWarning(leadName, hoursLeft, url = '/pipeline') {
    return this.show('تحذير: عميل سيُدوَّر!', {
      body: `العميل ${leadName} سيُدوَّر خلال ${hoursLeft} ساعة إذا لم يتم التواصل معه`,
      tag: 'rotation-' + Date.now(),
      data: { url }
    });
  }

  // Show general notification
  async showGeneral(title, body, url = '/') {
    return this.show(title, {
      body,
      tag: 'general-' + Date.now(),
      data: { url }
    });
  }
}

// Create singleton instance
const pushNotificationService = new PushNotificationService();

export default pushNotificationService;
